import { MdSave } from "react-icons/md";
import { ROUTES } from "../../../routes";
import DatePicker from "react-datepicker";
import { HiArrowLeft, HiX } from "react-icons/hi";
import ApiCohort from "../../../modules/api/cohort";
import ModulePopup from "../../../modules/popup/popup";
import FGField from "../../../components/dumb/FGField";
import FGInput from "../../../components/dumb/FGInput";
import { useEffect, useState, useContext } from "react";
import usePageTitle from "../../../hooks/use-page-title";
import FGToggle from "../../../components/dumb/FGToggle";
import Notifier from "../../../modules/notifier/notifier";
import DateRange from "../../../components/form/DateRange";
import Debouncer from "../../../modules/debouncer/debouncer";
import FGTextarea from "../../../components/dumb/FGTextarea";
import FGDropdown from "../../../components/dumb/FGDropdown";
import { BsBoxArrowInDown, BsFillExclamationTriangleFill } from "react-icons/bs";
import AppUserContext from "../../../context/app-user-context";
import FGDropdownApi from "../../../components/form/FGDropdownApi";
import Multilingual from "../../../modules/multilingual/multilingual";
import { PortalContext, usePortalSettingSet } from "../../../context/portal-context";
import FooterBoxAction from "../../../components/common/FooterBoxAction";
import ApiWLFormat, { TOptionItem } from "../../../modules/api/wl-format";
import PagePortalNotFound from "../PagePortalNotFound/PagePortalNotFound";
import { Link, useParams, useLocation, useNavigate } from "react-router-dom";

export default function PageCohortEdit() {
  const title = "Edit Cohort";
  usePageTitle(title);
  usePortalSettingSet("label_header", title);
  const { setCustomText } = useContext(PortalContext);
  const location = useLocation();
  const navigate = useNavigate();
  const user = useContext(AppUserContext);
  const { programmeId, _id } = useParams();
  const [contentChanged, setContentChanged] = useState(false);
  const [sending, setSending] = useState(false);
  const [found, setFound] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showWarning, setShowWarning] = useState(false);
  const [originalStartDate, setOriginalStartDate] = useState<Date | null>(null);
  const [originalEndDate, setOriginalEndDate] = useState<Date | null>(null);
  const [wlFormatOptions, setWlFormatOptions] = useState<TOptionItem[]>([]);
  const [wlFormatOption, setWlFormatOption] = useState<TOptionItem>(null);
  const [formData, setFormData] = useState({
    name: "",
    startDate: null,
    endDate: null,
    firstDate: null,
    weekly: 2,
    description: "",
    singleGroupMode: true,
    pdMode: false,
    wlFormat: null,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
  });
  const isValidProgrammeId = user.programmeIds.includes(programmeId);

  useEffect(() => {
    setCustomText(title);
  }, []);

  useEffect(() => {
    if (!isValidProgrammeId) {
      return;
    }
    ApiCohort.get(_id)
      .then((res) => {
        setLoading(false);
        if (res.result === "OK") {
          if (res.data.programme === programmeId) {
            setFound(true);
            setFormData((formData) => ({
              ...formData,
              name: res.data.name,
              startDate: new Date(res.data.startDate),
              endDate: new Date(res.data.endDate),
              firstDate: res.data.firstDate ? new Date(res.data.firstDate) : formData.firstDate,
              weekly: res.data.weekly ?? formData.weekly,
              description: res.data.description,
              singleGroupMode: res.data.singleGroupMode,
              pdMode: res.data.pdMode,
              wlFormat: res.data.wlFormat,
            }));
            setWlFormatOption(res.data.wlFormatOption);
            setOriginalStartDate(new Date(res.data?.startDate));
            setOriginalEndDate(new Date(res.data?.endDate));
          }
        } else {
          Notifier.error(res.message);
        }
      })
      .catch((error) => {
        setLoading(false);
        Notifier.error(error.message);
      });
  }, []);

  useEffect(() => {
    ApiWLFormat.getOptions(programmeId)
      .then((options) => {
        setWlFormatOptions(options);
      })
      .catch((error) => {
        Notifier.error(error.message);
      });
  }, []);

  const getWLFormatDropdownOptions = () => {
    let options = [...wlFormatOptions];
    if (
      wlFormatOption &&
      !options.some((option) => option.value === wlFormatOption.value)
    ) {
      options.splice(1, 0, wlFormatOption);
    }
    return options;
  };

  const handleOnChange = (e: any) => {
    setContentChanged(true);
    setFormData((d) => ({
      ...d,
      [e.target.name]: ["singleGroupMode", "pdMode"].includes(e.target.name)
        ? e.target.checked
        : e.target.value,
    }));
  };

  const handleDateChange = (start: Date, end: Date) => {
    end.setHours(23, 59, 59, 999);

    const dateChanged =
      (originalStartDate && start.toDateString() !== originalStartDate.toDateString()) ||
      (originalEndDate && end.toDateString() !== originalEndDate.toDateString());

    setShowWarning(dateChanged);
    setContentChanged(dateChanged);

    setFormData((prev) => ({
      ...prev,
      startDate: start,
      endDate: end,
    }));
  };

  const validateBeforeSubmit = () => {
    const errors = [];
    if (
      formData.endDate &&
      formData.startDate &&
      formData.endDate <= formData.startDate
    ) {
      errors.push("End date must be greater than start date");
    }
    if (
      formData.startDate &&
      formData.endDate &&
      (formData.firstDate < formData.startDate ||
        formData.firstDate > formData.endDate)
    ) {
      errors.push("First Submission Date must be from start date to end date");
    }
    if (!Number.isInteger(formData.weekly) || !(formData.weekly > 0)) {
      errors.push("Weekly Workplace Learning Report is required");
    }
    if (!formData.wlFormat) {
      errors.push("Please select Workplace Learning Report Format");
    }
    return errors;
  };

  const handleSubmit = (e: any) => {
    e.preventDefault();
    const errors = validateBeforeSubmit();
    if (errors.length > 0) {
      Notifier.error(errors[0]);
      return;
    }

    const dateChanged =
    (originalStartDate && formData.startDate?.toDateString() !== originalStartDate.toDateString()) ||
    (originalEndDate && formData.endDate?.toDateString() !== originalEndDate.toDateString());

    if (dateChanged) {
      ModulePopup.showAlertConfirm({
        title: "Confirmation",
        description: <p>You have changed the cohort start/end date. Are you sure you want to proceed?</p>,
        onYes() {
          processFormSubmission();
        },
      });
    } else {
      processFormSubmission();
    }
  };

  const processFormSubmission = () => {
    setSending(true);
    const payload = {
      ...formData,
      startDate: formData.startDate?.getTime(),
      endDate: formData.endDate?.getTime(),
      firstDate: formData.firstDate?.getTime(),
    };

    Debouncer.execute("UPDATE_DATA", async () => {
      ApiCohort.update(_id, payload)
        .then((res) => {
          setSending(false);
          if (res.result === "OK") {
            setContentChanged(false);
            setOriginalStartDate(formData.startDate);
            setOriginalEndDate(formData.endDate);
            Notifier.success(res.message);
          } else {
            Notifier.error(res.message);
          }
        })
        .catch((error) => {
          setSending(false);
          Notifier.error(error.message);
        });
    });
  };

  if (!isValidProgrammeId) {
    return <PagePortalNotFound />;
  }

  useEffect(() => {
    const isDateChanged =
      (originalStartDate && formData.startDate?.toDateString() !== originalStartDate.toDateString()) ||
      (originalEndDate && formData.endDate?.toDateString() !== originalEndDate.toDateString());

    setShowWarning(isDateChanged);
  }, [formData.startDate, formData.endDate, originalStartDate, originalEndDate]);

  return (
    <main className="form-generic content-generic">
      {/* <h1 className="fs-title hide-mobile-down">{title}</h1> */}

      {/* <div className="cg__actions hide-mobile-down">
        <Link
          to={`${ROUTES.PORTAL_COHORTS}/${programmeId}`}
          className="btn btn-primary"
          state={location.state}
          onClick={(e) => {
            if (
              contentChanged &&
              !window.confirm(Multilingual.get("CONFIRM_ACTION_CANT_BE_UNDONE"))
            ) {
              e.preventDefault();
            }
          }}
        >
          <HiArrowLeft /> Back
        </Link>
      </div> */}

      {loading && <p className="alert alert-info">Loading data...</p>}

      {!loading && !found && (
        <p className="alert alert-danger">Cohort not found.</p>
      )}

      {!loading && found && (
        <form onSubmit={handleSubmit} className="box-default bg-white p-3">
          {showWarning && (
            <div className="alert alert-warning" style={{ marginBottom: "1.5rem" }}>
              <BsFillExclamationTriangleFill size={20} color="#FF991F" style={{ marginRight: 7 }} />
              Updating Cohort Start and End Date after it’s being used by Students can make unintended behaviour. Please be careful.
            </div>
          )}
          <FGInput
            name="name"
            id="name"
            type="text"
            label="Name of the Cohort"
            required
            autoFocus
            value={formData.name}
            onChange={handleOnChange}
          />

          <FGTextarea
            name="description"
            id="description"
            label="Description of the Cohort"
            value={formData.description}
            onChange={handleOnChange}
          ></FGTextarea>

          <FGField variant="normal">
            <DateRange
              required
              title="Duration of the IWSP"
              start_date={formData.startDate}
              end_date={formData.endDate}
              onStartEndDateSelected={handleDateChange}
            />
          </FGField>

          <FGDropdownApi
            label="Workplace Learning Report Template"
            name="wlFormat"
            defaultValue={formData.wlFormat}
            options={getWLFormatDropdownOptions()}
            onChange={handleOnChange}
          />

          <FGField
            required
            name="firstDate"
            label={"End Date (Deadline) to submit the first Workplace Learning Report"}
          >
            <DatePicker
              placeholderText="Select Date"
              dateFormat="MMMM d, yyyy"
              selected={formData.firstDate}
              minDate={formData.startDate}
              maxDate={formData.endDate}
              onChange={(firstDate) => {
                firstDate.setHours(23, 59, 59, 999);
                setFormData((formData) => ({
                  ...formData,
                  firstDate: firstDate,
                }));
              }}
            />
          </FGField>

          <FGDropdown
            required
            name="weekly"
            label={
              "Students are required to submit a Workplace Learning Report every:"
            }
            defaultValue={`${formData.weekly}`}
            options={[
              {
                label: "1 week",
                value: "1",
              },
              {
                label: "2 weeks",
                value: "2",
              },
              {
                label: "3 weeks",
                value: "3",
              },
              {
                label: "4 weeks",
                value: "4",
              },
            ]}
            onChange={(e: any) =>
              setFormData((formData) => ({
                ...formData,
                weekly: parseInt(e.target.value),
              }))
            }
          />

          {/* We no longer support Group Mode: IS-145 */}
          {false && <FGToggle
              label="Single Group Mode"
              sublabel='When enabled, this cohort will have single group named "Default"'
              name="singleGroupMode"
              onChange={handleOnChange}
              checked={formData.singleGroupMode}
          ></FGToggle>}

          <FGToggle
            label="Professional Development Report using the GROW Model"
            sublabel="Enable the above setting if the Cohort of this Programme requires the Professional Development feature of GROW-Pro for Students and Supervisors."
            name="pdMode"
            onChange={handleOnChange}
            checked={formData.pdMode}
          ></FGToggle>

          <div>
            <button
              type="submit"
              className="btn btn-secondary"
              disabled={sending}
            >
              <BsBoxArrowInDown className="icon-pos-upper" /> Save
            </button>{" "}
            <Link
              to={`${ROUTES.PORTAL_COHORTS}/${programmeId}`}
              className="btn btn-primary"
              state={location.state}
              onClick={(e) => {
                if (
                  contentChanged &&
                  !window.confirm(
                    Multilingual.get("CONFIRM_ACTION_CANT_BE_UNDONE")
                  )
                ) {
                  e.preventDefault();
                }

                if (window.history.length > 1) {
                  e.preventDefault();
                  navigate(-1);
                }
              }}
            >
              <HiX /> Cancel
            </Link>
          </div>
        </form>
      )}

      <FooterBoxAction
        backUrlState={location.state}
        onBackUrlClick={(e) => {
          if (
            contentChanged &&
              !window.confirm(
                Multilingual.get("CONFIRM_ACTION_CANT_BE_UNDONE")
              )
          ) {
            e.preventDefault();
          }

          if (window.history.length === 1) {
            navigate(`${ROUTES.PORTAL_COHORTS}/${programmeId}`);
          } else {
            navigate(-1);
          }
          }
        }
      />
    </main>
  );
}
